/**
 * UnitBoard — la propiedad entera de un vistazo.
 *
 * Cada azulejo dice tres cosas y ni una más: la unidad, su estado y lo
 * siguiente que va a pasar. Todo lo demás espera a que se abra. El estado NO
 * es un campo editable: sale de las reservas, de los bloqueos y de la tarea de
 * limpieza (§8.1).
 *
 * El detalle se abre en línea, debajo del tablero, en vez de en un panel
 * lateral: en un móvil es un gesto menos y aquí no hace falta tapar la
 * pantalla para leer cuatro datos.
 */
import React from "react";
import { CaretDown } from "@phosphor-icons/react";
import { UNIT_STATE_LABEL, UNIT_STATE_TONE, HOUSEKEEPING_LABEL } from "@/data/todayV2Demo";
import {
  TodayBlock, TodayCard, StatusPill, Meta, QuickAction,
} from "@/pages/admin/pms/v2/V2Primitives";

const TILE_TONE = {
  red: "border-red-300 dark:border-red-900 bg-red-50/60 dark:bg-red-950/20",
  amber: "border-amber-300 dark:border-amber-900 bg-amber-50/50 dark:bg-amber-950/20",
  emerald: "border-stone-200 dark:border-stone-800",
  cyan: "border-cyan-200 dark:border-cyan-900 bg-cyan-50/40 dark:bg-cyan-950/20",
  violet: "border-violet-200 dark:border-violet-900 bg-violet-50/40 dark:bg-violet-950/20",
  blue: "border-blue-200 dark:border-blue-900",
  stone: "border-stone-200 dark:border-stone-800",
};

const DOT_TONE = {
  red: "bg-red-500",
  amber: "bg-amber-500",
  emerald: "bg-emerald-500",
  cyan: "bg-cyan-500",
  violet: "bg-violet-500",
  blue: "bg-blue-500",
  stone: "bg-stone-400",
};

export default function UnitBoard({
  board, unitLabel, selectedUnitId, onSelectUnit, showGuestNames, columns = 2,
}) {
  const selected = board.find((row) => row.unit.villa_id === selectedUnitId) || null;
  const grid = columns === 5
    ? "grid-cols-2 sm:grid-cols-3 lg:grid-cols-5"
    : "grid-cols-2";

  return (
    <TodayBlock
      id="today-board"
      title={`Estado de ${unitLabel.plural.toLowerCase()}`}
      count={board.length}
      testId="today-board"
    >
      <div className={`grid ${grid} gap-1.5`} data-testid="today-board-grid">
        {board.map((row) => (
          <UnitTile
            key={row.unit.villa_id}
            row={row}
            active={row.unit.villa_id === selectedUnitId}
            onSelect={onSelectUnit}
          />
        ))}
      </div>

      {selected && (
        <UnitDetail
          row={selected}
          unitLabel={unitLabel}
          showGuestNames={showGuestNames}
          onClose={() => onSelectUnit(null)}
        />
      )}
    </TodayBlock>
  );
}

function UnitTile({ row, active, onSelect }) {
  const tone = UNIT_STATE_TONE[row.state] || "stone";
  return (
    <button
      type="button"
      onClick={() => onSelect(active ? null : row.unit.villa_id)}
      data-testid={`unit-tile-${row.unit.villa_id}`}
      data-state={row.state}
      aria-expanded={active}
      className={`text-left rounded-lg border px-2.5 py-2 min-h-[72px] transition-colors ${TILE_TONE[tone]} ${
        active ? "ring-1 ring-stone-400 dark:ring-stone-500" : "hover:border-stone-400 dark:hover:border-stone-600"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-widest text-stone-500 dark:text-stone-400">
          {row.unit.villa_id}
        </span>
        <span className={`w-1.5 h-1.5 rounded-full ${DOT_TONE[tone]}`} aria-hidden="true" />
      </div>
      <div className="mt-0.5 text-[13px] text-stone-900 dark:text-stone-100 truncate">
        {UNIT_STATE_LABEL[row.state]}
      </div>
      <div className="text-[11px] text-stone-500 dark:text-stone-400 truncate">
        {row.detail}
      </div>
    </button>
  );
}

function UnitDetail({ row, unitLabel, showGuestNames, onClose }) {
  const stay = row.arriving || row.inStay || row.departing || null;
  return (
    <TodayCard className="mt-2" padding="p-3.5" testId={`unit-detail-${row.unit.villa_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">
            {unitLabel.singular} {row.unit.villa_id}
          </div>
          <div className="font-serif-dv text-lg text-stone-900 dark:text-stone-100 truncate">
            {row.unit.name}
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Cerrar detalle"
          data-testid={`unit-detail-close-${row.unit.villa_id}`}
          className="w-11 h-11 -mr-2 -mt-2 flex items-center justify-center text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 shrink-0"
        >
          <CaretDown size={16} weight="bold" />
        </button>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <StatusPill tone={UNIT_STATE_TONE[row.state]}>{UNIT_STATE_LABEL[row.state]}</StatusPill>
        {row.hk && (
          <StatusPill tone="stone" dot={false}>
            Limpieza: {HOUSEKEEPING_LABEL[row.hk.status].toLowerCase()}
          </StatusPill>
        )}
        {row.block && (
          <StatusPill tone="stone" dot={false}>
            Bloqueo {row.block.type === "OWNER_STAY" ? "por uso del propietario" : "de mantenimiento"}
            {" "}hasta el {row.block.end_date.slice(8)}
          </StatusPill>
        )}
      </div>

      <dl className="mt-3 space-y-1.5 text-sm">
        {stay && (
          <Fact label={row.arriving ? "Llegada de hoy" : (row.inStay ? "Estancia en curso" : "Salida de hoy")}>
            {showGuestNames ? stay.guest.name : `${stay.guest.adults + stay.guest.children} huéspedes`}
            <span className="text-stone-400"> · {stay.check_in} → {stay.check_out}</span>
          </Fact>
        )}
        {row.issue && (
          <Fact label="Incidencia que la bloquea">
            {row.issue.title}
            <span className="text-stone-400"> · {row.issue.issue_id}</span>
          </Fact>
        )}
        {!stay && !row.issue && <Fact label="Hoy">Sin movimientos.</Fact>}
      </dl>

      <Meta
        items={[`${row.unit.bedrooms} habitaciones`, `hasta ${row.unit.sleeps} personas`]}
        className="mt-2"
      />

      <div className="mt-3 flex flex-wrap gap-1.5">
        {stay && (
          <QuickAction
            label="Abrir reserva"
            to={`/pms/reservations/${stay.reservation_id}`}
            testId={`unit-detail-reservation-${row.unit.villa_id}`}
          />
        )}
        <QuickAction label="Calendario" to="/pms/calendar" testId={`unit-detail-calendar-${row.unit.villa_id}`} />
        {row.issue && (
          <QuickAction label="Incidencia" to="/pms/issues" testId={`unit-detail-issue-${row.unit.villa_id}`} />
        )}
      </div>
    </TodayCard>
  );
}

function Fact({ label, children }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
      <dt className="text-[11px] uppercase tracking-widest text-stone-400 sm:w-40 shrink-0">
        {label}
      </dt>
      <dd className="text-stone-700 dark:text-stone-300 min-w-0">{children}</dd>
    </div>
  );
}
